import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {IElement} from '../../../../incase-domain/elements';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import {getElementData} from '../../../../incase-domain/elements/base/elementsData';
import ElementContextMenuTrigger from '../../../../ui/components/contextMenu/ElementContextMenu';
import {setSidePanelContentAction} from '../../../../store/actions/grid';
import {SidePanelContent} from '../../../../store/actions/types/grid.types';
import {setContextSelectedElementIndex, setHighlightedElementIndex} from '../../../../store/actions/slide';
import Button from '../../../../incase-domain/components/button/Button';
import useElementActions from '../../../../utils/hooks/slideUpdates/useElementActions';
import {contextSelectedElementIndexSelector, highlightedElementIndexSelector} from '../../../../store/selectors/slide.selectors';
import InputWithCharacterCounter from '../../highlighted/common/InputWithCharacterCounter';

interface ElementListProps {
    elements: IElement[];
}

function ElementList({elements}: ElementListProps): JSX.Element {
    const dispatch = useDispatch();
    const highlightedElementIndex = useSelector(highlightedElementIndexSelector);
    const contextSelectedElementIndex = useSelector(contextSelectedElementIndexSelector);

    const {updateElement} = useElementActions();

    const [selectedElementIndex, setSelectedElementIndex] = useState<number | null>(null);
    const [elementName, setElementName] = useState<string>('');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setSelectedElementIndex(highlightedElementIndex);
    }, [highlightedElementIndex]);

    useLayoutEffect(() => {
        if (contextSelectedElementIndex !== null) {
            setElementName(elements[contextSelectedElementIndex]?.name ?? '');
        }
    }, [contextSelectedElementIndex, elements]);

    useEffect(() => {
        if (contextSelectedElementIndex !== null) {
            inputRef.current?.focus();
        }
    }, [contextSelectedElementIndex]);

    const getElementIcon = (type: ELEMENT): string => {
        return getElementData(type)?.icon || '';
    };

    const handleOpenElementSidePanel = (index: number): void => {
        dispatch(setSidePanelContentAction(SidePanelContent.HIGHLIGHTED_ELEMENT));
        dispatch(setHighlightedElementIndex(index));
    };

    const handleUpdateElementName = (index: number): void => {
        updateElement(index, {
            ...elements[index],
            name: elementName,
        });
        dispatch(setContextSelectedElementIndex(null));
    };

    return (
        <div className="c-elementList">
            {elements.map((element, index) => (
                <ElementContextMenuTrigger key={element.id} element={element} elementIndex={index} inHighlightedMode>
                    <Button
                        className="c-elementList__elementTile"
                        variant="ghost"
                        color={selectedElementIndex === index ? 'gray_100' : undefined}
                        size="sm"
                        fullWidth
                        leading={getElementIcon(element.type)}
                        onClick={() => {
                            setSelectedElementIndex(index);
                            dispatch(setHighlightedElementIndex(index));
                        }}
                        onDoubleClick={() => handleOpenElementSidePanel(index)}
                    >
                        {contextSelectedElementIndex === index ? (
                            <InputWithCharacterCounter
                                ref={inputRef}
                                value={elementName}
                                onChange={setElementName}
                                enableSubmitByEnter
                                onEnterPress={() => handleUpdateElementName(index)}
                                onBlur={() => handleUpdateElementName(index)}
                                maxLength={25}
                            />
                        ) : (
                            <span className="c-elementList__elementName">{element.name}</span>
                        )}
                    </Button>
                </ElementContextMenuTrigger>
            ))}
        </div>
    );
}

export default ElementList;
